import { cn } from '@/utils/cn'

type Severity = 'low' | 'medium' | 'high' | 'critical'

interface ContributingFeature {
  feature: string
  contribution: number
}

interface AnomalyAlertProps {
  isAnomaly: boolean
  anomalyScore: number
  anomalyType: string
  severity: Severity
  contributingFeatures: ContributingFeature[]
  className?: string
}

const SEVERITY_STYLES: Record<Severity, { bg: string; border: string; text: string; badge: string }> = {
  low: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-800 dark:text-blue-300',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  },
  medium: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    text: 'text-yellow-800 dark:text-yellow-300',
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  },
  high: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    border: 'border-orange-200 dark:border-orange-800',
    text: 'text-orange-800 dark:text-orange-300',
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  },
  critical: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-800 dark:text-red-300',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  },
}

export function AnomalyAlert({
  isAnomaly,
  anomalyScore,
  anomalyType,
  severity,
  contributingFeatures,
  className,
}: AnomalyAlertProps) {
  // Only show alert when isAnomaly is true
  if (!isAnomaly) {
    return null
  }

  const styles = SEVERITY_STYLES[severity]

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        styles.bg,
        styles.border,
        className
      )}
      role="alert"
      aria-label={`Anomaly detected: ${anomalyType}, severity ${severity}`}
    >
      <div className="flex items-start gap-3">
        {/* Alert icon */}
        <div className="flex-shrink-0 mt-0.5">
          <svg
            className={cn('w-5 h-5', styles.text)}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          {/* Header row with severity badge and score */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className={cn(
                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                styles.badge
              )}
            >
              {severity.charAt(0).toUpperCase() + severity.slice(1)}
            </span>
            <span className={cn('text-sm font-semibold', styles.text)}>
              Anomaly Detected
            </span>
          </div>

          {/* Anomaly type and score */}
          <div className="flex items-center gap-4 mb-2">
            <p className={cn('text-sm', styles.text)}>
              Type: <span className="font-medium">{anomalyType}</span>
            </p>
            <p className={cn('text-sm', styles.text)}>
              Score: <span className="font-medium">{(anomalyScore * 100).toFixed(1)}%</span>
            </p>
          </div>

          {/* Contributing features */}
          {contributingFeatures.length > 0 && (
            <div className="mt-2">
              <p className={cn('text-xs font-medium mb-1', styles.text)}>
                Contributing Factors:
              </p>
              <ul className="space-y-1" aria-label="Contributing features">
                {contributingFeatures.map(({ feature, contribution }) => (
                  <li
                    key={feature}
                    className={cn('text-xs flex items-center gap-2', styles.text)}
                  >
                    <span className="w-2 h-2 rounded-full bg-current flex-shrink-0" aria-hidden="true" />
                    <span className="truncate">{feature}</span>
                    <span className="ml-auto text-xs opacity-75">
                      {(contribution * 100).toFixed(0)}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
